import { useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, ColorPicker, TextArea, TextField } from '@fly/core';
import { useFlyForm } from '@fly/shared-hooks';
import { closeDialog, useAppDispatch } from '@fly/store';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Environment } from '../../store';

function useEnvironmentFormSchema(existingEnvironmentNames?: string[]) {
  const { t } = useTranslation();

  return useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(
            1,
            t('app.common.fields.validation.required', {
              defaultValue: 'Field required',
            })
          )
          .regex(
            /^[a-zA-Z0-9-_]{1,100}$/,
            t(
              'app.dashboard.runtime_environments.environment_form.errors.env_name_validation',
              {
                defaultValue:
                  'Environment name must be 1-100 characters and contain only alphanumeric characters, hyphens, or underscores',
              }
            )
          )
          .refine((value) => !existingEnvironmentNames?.includes(value), {
            message: t(
              'app.dashboard.runtime_environments.environment_form.errors.env_name_taken',
              {
                defaultValue: 'This environment name is already taken',
              }
            ),
          }),
        color: z.string().min(1),
        description: z.string(),
      }),
    [t, existingEnvironmentNames]
  );
}

export type EnvironmentFormData = z.infer<
  ReturnType<typeof useEnvironmentFormSchema>
>;

export function EnvironmentForm({
  initialValues,
  onSubmit,
  submitText,
  existingEnvironmentNames,
}: {
  initialValues?: Environment;
  onSubmit: (data: EnvironmentFormData) => void;
  submitText?: string;
  existingEnvironmentNames?: string[];
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const environmentFormSchema = useEnvironmentFormSchema(
    existingEnvironmentNames
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useFlyForm<EnvironmentFormData>({
    resolver: zodResolver(environmentFormSchema),
    defaultValues: {
      name: initialValues?.name || '',
      color: initialValues?.color || '#3b82f6',
      description: initialValues?.description || '',
    },
  });

  const handleFormCancel = useCallback(() => {
    dispatch(closeDialog());
  }, [dispatch]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-fly-5'>
      <div className='flex flex-col gap-fly-3'>
        <div className='flex w-full flex-row items-start gap-fly-3'>
          <Controller
            name='name'
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                data-testid='runtime-environments-env-name'
                size='2'
                className='grow'
                label={t(
                  'app.dashboard.runtime_environments.environment_form.name.label',
                  { defaultValue: 'Environment Name' }
                )}
                placeholder={t(
                  'app.dashboard.runtime_environments.environment_form.name.placeholder',
                  { defaultValue: 'Ex. Staging' }
                )}
                error={!!fieldState.error}
                errorMessage={fieldState.error?.message}
                required
                parse={(value) => value.toLowerCase().trim()}
                {...field}
              />
            )}
          />
          <Controller
            name='color'
            control={control}
            render={({ field: { onChange, ref, value, ...field } }) => (
              <ColorPicker
                onValueChange={onChange}
                defaultValue={initialValues?.color || '#3b82f6'}
                defaultFormat='hex'
                className='mt-fly-7'
                {...field}
              />
            )}
          />
        </div>
      </div>
      <Controller
        name='description'
        control={control}
        render={({ field }) => (
          <TextArea
            data-testid='runtime-environments-env-desc'
            size='2'
            placeholder={t(
              'app.dashboard.runtime_environments.environment_form.description.placeholder',
              { defaultValue: 'Add an optional description' }
            )}
            label={t(
              'app.dashboard.runtime_environments.environment_form.description.label',
              { defaultValue: 'Description (Optional)' }
            )}
            {...field}
          />
        )}
      />
      <div className='mt-fly-5 flex flex-row justify-end gap-fly-3'>
        <Button
          data-testid='runtime-environments-env-cancel'
          type='button'
          variant='soft'
          color='neutral'
          onClick={handleFormCancel}
        >
          {t('common.actions.cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          data-testid='runtime-environments-env-submit'
          type='submit'
          loading={isSubmitting}
        >
          {submitText || t('common.actions.save', { defaultValue: 'Save' })}
        </Button>
      </div>
    </form>
  );
}
